import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
const BASE_URL = process.env.REACT_APP_BASE_URL;
const EditCourse = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [courseName, setCourseName] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const [courseImage, setCourseImage] = useState(null);
  const [courseImagePreview, setCourseImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [courseType, setCourseType] = useState("free");
  const [price, setPrice] = useState("");

  // Fetch categories
  useEffect(() => {
    fetch(`${BASE_URL}/categories/list/`,{
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        }
    })
      .then((res) => res.json())
      .then((data) => setCategories(data))
      .catch((err) => console.error(err));
  }, []);

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await fetch(`${BASE_URL}/courses/${id}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_X_API_KEY
          },
        });
        if (!response.ok) throw new Error("Failed to fetch course");
        const data = await response.json();

        setCourseName(data.name);
        setCourseDescription(data.description);
        setSelectedCategory(data.category);
        setSelectedSubcategory(data.subcategory);
        setCourseType(data.course_type.toLowerCase());
        setPrice(data.price || "");
        setCourseImagePreview(data.image);

        // Pre-fill subcategories for selected category
        const category = data.category
          ? categories.find((cat) => cat.id === data.category)
          : null;
        setSubcategories(category ? category.subcategories : []);
      } catch (err) {
        console.error(err);
      }
    };

    if (id) fetchCourse();
  }, [id, categories]);

  const handleCategoryChange = (e) => {
    const categoryId = parseInt(e.target.value);
    setSelectedCategory(categoryId);
    const category = categories.find((cat) => cat.id === categoryId);
    setSubcategories(category ? category.subcategories : []);
    setSelectedSubcategory(null);
  };

  const handleImageChange = (file) => {
    setCourseImage(file);
    setCourseImagePreview(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    setCourseImage(null);
    setCourseImagePreview(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) handleImageChange(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      formData.append("name", courseName);
      formData.append("description", courseDescription);
      formData.append("category", selectedCategory || "");
      formData.append("subcategory", selectedSubcategory || "");
      formData.append("price", price);
      formData.append("course_type", courseType);

      if (courseImage) formData.append("image", courseImage);

      const response = await fetch(`${BASE_URL}/courses/${id}/update/`, {
        method: "PUT",
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus("success");
        navigate("/teacher/course");
      } else {
        throw new Error(data.message || "Failed to update course");
      }
    } catch (err) {
      console.error(err);
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    courseName && courseDescription && selectedCategory && selectedSubcategory;

  return (
   <div className="max-w-5xl mx-auto mt-8 py-8 px-3 md:px-8 bg-white rounded-2xl shadow-sm border border-gray-100">
  {/* Header */}
  <div className="flex items-center gap-3 mb-8">
    <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
      <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    </div>
    <div>
      <h2 className="text-xl font-semibold text-gray-900">Edit Course</h2>
      <p className="text-sm text-gray-500 mt-0.5">Update your course information</p>
    </div>
  </div>

  {submitStatus === "success" && (
    <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl mb-6">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-sm font-medium">Course updated successfully!</span>
    </div>
  )}

  {submitStatus === "error" && (
    <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl mb-6">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="text-sm font-medium">Failed to update course. Please try again.</span>
    </div>
  )}

  <form onSubmit={handleSubmit} className={isLoading ? "opacity-70 pointer-events-none" : "space-y-6"}>

    {/* Course Name */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Course Name <span className="text-red-500">*</span>
        </label>
      </div>
      <input
        type="text"
        value={courseName}
        onChange={(e) => setCourseName(e.target.value)}
        placeholder="e.g. Introduction to Web Development"
        required
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
      />
    </div>

    {/* Course Image */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Course Image
        </label>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById("fileInput")?.click()}
        className={`border-2 border-dashed rounded-xl p-6 flex items-center justify-center cursor-pointer transition-all relative bg-gray-50 min-h-[160px]
          ${isDragging
            ? "border-[#0f6abf] bg-[#e8f0f7]"
            : "border-gray-300 hover:border-[#0f6abf] hover:bg-gray-100"
          }`}
      >
        {courseImagePreview ? (
          <div className="relative w-full flex items-center justify-center">
            <img
              src={courseImagePreview}
              alt="Course preview"
              className="max-h-32 max-w-full object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearImage();
              }}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 font-medium">Drag & drop image here or click to upload</p>
            <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
          </div>
        )}

        <input
          type="file"
          id="fileInput"
          accept="image/*"
          hidden
          onChange={(e) =>
            e.target.files &&
            e.target.files[0] &&
            handleImageChange(e.target.files[0])
          }
        />
      </div>
    </div>

    {/* Description */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Course Description <span className="text-red-500">*</span>
        </label>
      </div>
      <textarea
        value={courseDescription}
        onChange={(e) => setCourseDescription(e.target.value)}
        placeholder="Describe your course, learning objectives, prerequisites..."
        required
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all min-h-[120px] resize-y"
      />
    </div>

    {/* Category */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Main Category <span className="text-red-500">*</span>
        </label>
      </div>
      <select
        required
        onChange={handleCategoryChange}
        value={selectedCategory || ""}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all appearance-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2390A3BF' stroke-width='1.5'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 16px center'
        }}
      >
        <option value="">Select Category</option>
        {categories.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>
    </div>

    {/* Subcategory */}
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Subcategory <span className="text-red-500">*</span>
        </label>
      </div>
      <select
        required
        disabled={!selectedCategory}
        onChange={(e) => setSelectedSubcategory(Number(e.target.value))}
        value={selectedSubcategory || ""}
        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all appearance-none disabled:bg-gray-100 disabled:text-gray-500"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2390A3BF' stroke-width='1.5'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 16px center'
        }}
      >
        <option value="">Select Subcategory</option>
        {subcategories.map((sub) => (
          <option key={sub.id} value={sub.id}>
            {sub.name}
          </option>
        ))}
      </select>
    </div>

    {/* Course Type */}
    <div className="pt-2">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
        <label className="text-sm font-semibold text-gray-700">
          Course Type
        </label>
      </div>
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="courseType"
            value="free"
            checked={courseType === "free"}
            onChange={() => setCourseType("free")}
            className="w-4 h-4 text-[#0f6abf] border-gray-300 focus:ring-[#0f6abf]/20"
          />
          <span className="text-sm text-gray-700">Free</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="courseType"
            value="paid"
            checked={courseType === "paid"}
            onChange={() => setCourseType("paid")}
            className="w-4 h-4 text-[#0f6abf] border-gray-300 focus:ring-[#0f6abf]/20"
          />
          <span className="text-sm text-gray-700">Paid</span>
        </label>
      </div>
    </div>

    {/* Price */}
    {courseType === "paid" && (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
          <label className="text-sm font-semibold text-gray-700">
            Price <span className="text-red-500">*</span>
          </label>
        </div>
        <input
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="Enter price"
          required
          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
        />
      </div>
    )}

    {/* Submit */}
    <div className="pt-4">
      <button
        type="submit"
        disabled={!isFormValid || isLoading}
        className="w-full py-3 px-6 bg-[#0f6abf] text-white font-semibold rounded-xl hover:bg-[#0a5ca8] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isLoading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Updating Course...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            Update Course
          </>
        )}
      </button>
    </div>

  </form>
</div>

  );
};

export default EditCourse;
